import { ChangeEvent, Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { TextboxTypes } from './_types';
import { Headline6 } from '../../MDC/Typography/Typography';
import Icon from '../../Icon/Icon';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import classes from './Textbox.module.scss';

const ValidationTextbox = props => {
	const { formDetails } = props;
	const [autoValues, setAutoValues] = useState([]);
	const [showAutoOptions, setShowAutoOptions] = useState(true);
	const [inputFocus, setInputFocus] = useState(false);
	const showValidation = formDetails.validation.touched;
	const validationMessage = showValidation ? props.validationMessage : null;
	const validity = showValidation
		? !(props.validationMessage && props.validationMessage.length > 0) &&
		  props.valid
		: true;
	const iconStyle = validity
		? 'validation__icon--valid'
		: 'validation__icon--invalid';
	const iconShape = validity ? 'check_circle_outline' : 'block';
	const combinedTextboxStyles = [];
	if (props.className) {
		combinedTextboxStyles.push(props.className);
	}
	combinedTextboxStyles.push(classes['input__textbox--partial-width']);
	const combinedTextboxStyle = combinedTextboxStyles.join(' ');

	const inputRef = useRef();

	/**
	 * handles input updates after key is struck
	 * @param {ChangeEvent<HTMLInputElement>} e input event
	 */
	const handleKeyUp = e => {
		formDetails.settings
			.autocomplete(e.target.value)
			.then(values => setAutoValues(values));
		if (e.key === 'Enter') {
			setShowAutoOptions(false);
		}
	};

	/**
	 * handles selection of an autocomplete option
	 * @param {string} value autocompletion value
	 */
	const handleAutoSelection = value => {
		props.onChange({ target: { value: value } }, formDetails.entryKey);
		inputRef.current.focus();
	};

	/**
	 * update whether to show auto-options
	 */
	useEffect(() => {
		if (!inputFocus) {
			setShowAutoOptions(false);
		} else {
			setShowAutoOptions(true);
		}
	}, [inputFocus]);

	//render based on TextboxTypes
	if (formDetails.settings.type === TextboxTypes.AUTOCOMPLETE) {
		return (
			<Fragment>
				<StyleAuxiliary classes={classes['input__textbox--full-width']}>
					<Icon icon={iconShape} className={classes[iconStyle]} />
					<input
						{...props}
						ref={inputRef}
						className={combinedTextboxStyle}
						autocomplete={false}
						onKeyUp={e => handleKeyUp(e)}
						onFocus={() => setInputFocus(true)}
						onBlur={() => {
							props.onBlur();
							setInputFocus(false);
						}}
					/>

					<StyleAuxiliary
						classes={classes['input__validation--invalid']}
					>
						{validationMessage &&
							validationMessage
								.split('\n')
								.map((s, idx) => (
									<Headline6 key={`${s}${idx}`}>
										{s}
									</Headline6>
								))}
					</StyleAuxiliary>
					<StyleAuxiliary classes={classes['input__autocomplete']}>
						{showAutoOptions
							? autoValues.map(val => (
									<div
										onMouseDown={() =>
											handleAutoSelection(val)
										}
									>
										{val}
									</div>
							  ))
							: null}
					</StyleAuxiliary>
				</StyleAuxiliary>
			</Fragment>
		);
	} else {
		return (
			<Fragment>
				<StyleAuxiliary classes={classes['input__textbox--full-width']}>
					<Icon icon={iconShape} className={classes[iconStyle]} />
					<input {...props} className={combinedTextboxStyle} />
					<StyleAuxiliary
						classes={classes['input__validation--invalid']}
					>
						{validationMessage &&
							validationMessage
								.split('\n')
								.map((s, idx) => (
									<Headline6 key={`${s}${idx}`}>
										{s}
									</Headline6>
								))}
					</StyleAuxiliary>
				</StyleAuxiliary>
			</Fragment>
		);
	}
};

ValidationTextbox.propTypes = {
	autocomplete: PropTypes.string,
	autofocus: PropTypes.bool,
	className: PropTypes.string,
	dirname: PropTypes.string,
	disabled: PropTypes.bool,
	form: PropTypes.string,
	id: PropTypes.string,
	includeIcon: PropTypes.bool,
	key: PropTypes.string,
	list: PropTypes.string,
	max: PropTypes.number,
	maxlength: PropTypes.number,
	min: PropTypes.number,
	minlength: PropTypes.number,
	multiple: PropTypes.bool,
	name: PropTypes.string,
	onChange: PropTypes.func,
	pattern: PropTypes.string,
	placeholder: PropTypes.string,
	readonly: PropTypes.bool,
	required: PropTypes.bool,
	size: PropTypes.number,
	step: PropTypes.number,
	type: PropTypes.oneOf(Object.values(TextboxTypes)),
	value: PropTypes.string,
	valid: PropTypes.bool,
	validationMessage: PropTypes.string,
};

export default ValidationTextbox;
